// « Fiche client » — one client of the cardex (PMS-CARDEX-BRIEF §6.1 S3–S5).
//
// Two halves, owned by two systems until go-live:
//
//   * IDENTITY — name, e-mail, telephones, address, country. Vega's, while the
//     client has a Vega number and the établissement is not live (`vegaHeld`):
//     the nightly seed keeps them in step with Vega, so an edit here would be
//     silently reverted at 02:40. Read-only, with the sentence that says where
//     to correct them. At cutover they open up without a code change.
//   * ACCOUNT — type, sous-type, SIREN, TVA, language, notes. The PMS's from the
//     start: Vega holds no SIREN and no sous-type at all.
//
// Only what CHANGED is sent (`pmsGuestSheetPatch`), so a held identity never
// travels; the pure rules run before the save, so a bad SIREN key is named
// under its field rather than in a sentence back from the database.

import { useEffect, useMemo, useState } from 'react';
import {
  PMS_CUSTOMER_TYPE_LABELS_FR,
  pmsGuestSheetForm,
  pmsGuestSheetPatch,
  pmsValidateGuestSheet,
  type CustomerSubtype,
  type GuestId,
  type GuestService,
  type GuestSheet,
  type GuestSheetForm,
} from '@lemontaigu/pms-core';
import { agoLabelFr, fmtDate } from './logic.js';
import { todayParis } from './departureSheet.js';

export interface GuestSheetPanelProps {
  guests: Pick<GuestService, 'sheet' | 'save'>;
  id: GuestId;
  subtypes: CustomerSubtype[];
  canEdit: boolean;
  onClose: () => void;
  /** After a save — the list re-reads the row. */
  onSaved: () => void;
}

const LANGS = [
  ['fr', 'Français'],
  ['en', 'Anglais'],
  ['es', 'Espagnol'],
  ['de', 'Allemand'],
] as const;

export function GuestSheetPanel({ guests, id, subtypes, canEdit, onClose, onSaved }: GuestSheetPanelProps) {
  const [sheet, setSheet] = useState<GuestSheet | null>(null);
  const [form, setForm] = useState<GuestSheetForm | null>(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setSheet(null);
    setForm(null);
    setError('');
    void guests.sheet(id).then((r) => {
      if (cancelled) return;
      if (!r.ok) {
        setError(r.error.message);
        return;
      }
      setSheet(r.value);
      setForm(pmsGuestSheetForm(r.value));
    });
    return () => {
      cancelled = true;
    };
  }, [guests, id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const patch = useMemo(() => (sheet && form ? pmsGuestSheetPatch(sheet, form) : {}), [sheet, form]);
  const errors = useMemo(() => (sheet ? pmsValidateGuestSheet(sheet, patch) : {}), [sheet, patch]);
  const dirty = Object.keys(patch).length > 0;
  const invalid = Object.keys(errors).length > 0;

  const set = (p: Partial<GuestSheetForm>) => setForm((f) => (f ? { ...f, ...p } : f));

  const save = async () => {
    if (!sheet) return;
    setSaving(true);
    setError('');
    const r = await guests.save(sheet, patch);
    setSaving(false);
    if (!r.ok) {
      setError(r.error.message);
      return;
    }
    onSaved();
    // Re-read rather than trust the draft: the database normalises (spaces out
    // of a SIREN, a Particulier's sous-type dropped) and the fiche should show
    // what is stored.
    const again = await guests.sheet(id);
    if (again.ok) {
      setSheet(again.value);
      setForm(pmsGuestSheetForm(again.value));
    }
  };

  const title = sheet ? `${sheet.lastName.toUpperCase()} ${sheet.firstName}`.trim() : 'Fiche client';
  const lockIdentity = !canEdit || (sheet?.vegaHeld ?? true);
  const lockAccount = !canEdit;
  // A retired sous-type still labels the clients that carry it.
  const subtypeOptions = subtypes.filter((s) => s.active || s.code === sheet?.customerSubtype);

  const text = (
    field: keyof GuestSheetForm & keyof typeof errors,
    label: string,
    opts: { wide?: boolean; type?: string; locked: boolean; placeholder?: string },
  ) =>
    form && (
      <label className={opts.wide ? 'pms-sheet-wide' : undefined}>
        {label}
        <input
          type={opts.type ?? 'text'}
          value={form[field] as string}
          disabled={opts.locked}
          placeholder={opts.placeholder}
          aria-invalid={errors[field] ? true : undefined}
          onChange={(e) => set({ [field]: e.target.value } as Partial<GuestSheetForm>)}
        />
        {errors[field] && <span className="pms-field-err">{errors[field]}</span>}
      </label>
    );

  return (
    <aside className="pms-sheet pms-guest-sheet" aria-label={`Fiche client ${title}`}>
      <header className="pms-sheet-head">
        <h3>
          {title}
          {sheet && (
            <span className="pms-sheet-floor">
              {PMS_CUSTOMER_TYPE_LABELS_FR[sheet.customerType]}
              {sheet.sourceVegaRef && ` · Vega ${sheet.sourceVegaRef}`}
            </span>
          )}
        </h3>
        <button type="button" className="pms-sheet-close" onClick={onClose} aria-label="Fermer">
          ✕
        </button>
      </header>

      {!canEdit && <p className="pms-hint">Lecture seule — la modification demande le droit « fiche client ».</p>}
      {sheet?.cardexPass === 2 && (
        <p className="pms-warn pms-sheet-consent">
          Consentement requis — client vu il y a 3 à 5 ans, repris avant la campagne de consentement : sa fiche ne
          se conserve qu’avec son accord.
        </p>
      )}
      {error && <p className="auth-err">Erreur : {error}</p>}
      {!sheet && !error && <p className="pms-hint">Chargement…</p>}

      {sheet && form && (
        <div className="pms-sheet-body">
          <p className="pms-sheet-section">
            Identité et coordonnées
            {sheet.vegaHeld && (
              <span className="pms-hint">
                Tenues à jour depuis Vega jusqu’à la mise en service : elles se corrigent dans Vega, et la correction
                arrive ici la nuit suivante.
              </span>
            )}
          </p>
          {text('lastName', 'Nom', { locked: lockIdentity })}
          {text('firstName', 'Prénom', { locked: lockIdentity })}
          {text('email', 'E-mail', { wide: true, type: 'email', locked: lockIdentity })}
          {text('phone', 'Téléphone', { type: 'tel', locked: lockIdentity })}
          {text('mobile', 'Portable', { type: 'tel', locked: lockIdentity })}
          {text('addressLine1', 'Adresse', { wide: true, locked: lockIdentity })}
          {text('addressLine2', 'Complément d’adresse', { wide: true, locked: lockIdentity })}
          {text('postalCode', 'Code postal', { locked: lockIdentity })}
          {text('city', 'Ville', { locked: lockIdentity })}
          {text('countryCode', 'Pays', { locked: lockIdentity, placeholder: 'FR' })}
          <label>
            Langue
            <select
              value={form.lang}
              disabled={lockAccount}
              onChange={(e) => set({ lang: e.target.value as GuestSheetForm['lang'] })}
            >
              {LANGS.map(([code, label]) => (
                <option key={code} value={code}>
                  {label}
                </option>
              ))}
            </select>
          </label>

          <p className="pms-sheet-section">Compte</p>
          <label>
            Type de client
            <select
              value={form.customerType}
              disabled={lockAccount}
              onChange={(e) => set({ customerType: e.target.value as GuestSheetForm['customerType'] })}
            >
              <option value="particulier">{PMS_CUSTOMER_TYPE_LABELS_FR.particulier}</option>
              <option value="societe">{PMS_CUSTOMER_TYPE_LABELS_FR.societe}</option>
            </select>
          </label>
          {form.customerType === 'societe' ? (
            <label>
              Sous-type
              <select
                value={form.customerSubtype}
                disabled={lockAccount}
                onChange={(e) => set({ customerSubtype: e.target.value })}
              >
                <option value="">— non précisé</option>
                {subtypeOptions.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.labelFr}
                    {s.active ? '' : ' (retiré)'}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <span />
          )}
          {text('siren', 'SIREN', { locked: lockAccount, placeholder: '9 chiffres' })}
          {text('vatNumber', 'N° TVA intracommunautaire', { locked: lockAccount, placeholder: 'FR…' })}
          {form.customerType === 'societe' && form.siren.trim() === '' && (
            <p className="pms-hint pms-sheet-wide">
              Sans SIREN, une facture à une société française ne pourra pas être émise une fois la facturation
              électronique en vigueur.
            </p>
          )}

          <label className="pms-sheet-wide">
            Notes
            <textarea
              rows={4}
              value={form.notes}
              disabled={lockAccount}
              onChange={(e) => set({ notes: e.target.value })}
            />
          </label>
        </div>
      )}

      <footer className="pms-sheet-foot">
        <span className="pms-sheet-trace">
          {sheet?.cardexLastActivity &&
            `Dernière activité Vega le ${fmtDate(sheet.cardexLastActivity)} (${agoLabelFr(
              sheet.cardexLastActivity,
              todayParis(new Date()),
            )}) · `}
          {sheet?.cardexLoadedAt
            ? `Cardex Vega, passe ${sheet.cardexPass ?? '?'}, chargé le ${fmtDate(sheet.cardexLoadedAt.slice(0, 10))}`
            : sheet?.sourceVegaRef
              ? 'Client Vega hors cardex'
              : sheet
                ? 'Créé dans le PMS'
                : ''}
          {sheet && ` · modifié le ${fmtDate(sheet.updatedAt.slice(0, 10))}`}
        </span>
        <div className="pms-sheet-actions">
          <button type="button" className="auth-btn is-ghost" onClick={onClose} disabled={saving}>
            {canEdit ? 'Annuler' : 'Fermer'}
          </button>
          {canEdit && (
            <button
              type="button"
              className="auth-btn"
              disabled={!dirty || invalid || saving}
              onClick={() => void save()}
            >
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          )}
        </div>
      </footer>
    </aside>
  );
}
